import { getCharacter, getTheme, getThemeRules, type Theme } from '@secretnenshi/content';
import {
  EnactmentSource,
  ExecutivePower,
  type Faction,
  type GameLogEntry,
  type GameView,
  type PolicyType,
  type SpeechClaim,
  type ThemeId,
  type WinReason,
} from '@secretnenshi/shared';

/*
 * All player-facing game words come from the current theme (game variant).
 * `makeCopy(themeId)` returns the words plus helpers that assemble sentences
 * from them; components get it through useTheme().
 */

export function nameOf(view: GameView, playerId: string | undefined): string {
  return view.players.find((p) => p.id === playerId)?.name ?? 'Someone';
}

export function formatClock(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Fill {placeholders} in a theme string. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

function joinNames(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}

const NUMBER_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

type PowerKey = 'CAMPAIGN_RESEARCH' | 'INTERNAL_POLLING' | 'UNITY_GOVERNMENT' | 'COURT_CHALLENGE';

/** Engine power -> the theme's term for it. */
function powerKey(power: ExecutivePower): PowerKey | null {
  switch (power) {
    case ExecutivePower.CAMPAIGN_RESEARCH:
    case ExecutivePower.INTERNAL_POLLING:
    case ExecutivePower.UNITY_GOVERNMENT:
    case ExecutivePower.COURT_CHALLENGE:
      return power;
    case ExecutivePower.COURT_CHALLENGE_AND_VETO:
      return ExecutivePower.COURT_CHALLENGE;
    default:
      return null;
  }
}

export function makeCopy(themeId: ThemeId) {
  const theme: Theme = getTheme(themeId);
  const t = theme.text;
  const { minister, premier } = t.offices;

  const faction = (f: Faction) => t.factions[f].name;
  const factionPlural = (f: Faction) => t.factions[f].plural;
  const policy = (p: PolicyType) => t.policies[p].name;
  const power = (p: ExecutivePower) => {
    const k = powerKey(p);
    return k ? t.powers[k].name : '';
  };
  /** Track label, e.g. "Court + Veto". */
  const powerShort = (p: ExecutivePower) => {
    if (p === ExecutivePower.COURT_CHALLENGE_AND_VETO) return `${t.powers.COURT_CHALLENGE.short} + ${t.powers.VETO.short}`;
    const k = powerKey(p);
    return k ? t.powers[k].short : '';
  };
  const powerNarrator = (p: ExecutivePower) => {
    const k = powerKey(p);
    return k ? t.powers[k].narrator : '';
  };
  const win = (r: WinReason) => t.wins[r];
  const rules = (playerCount: number) => getThemeRules(theme.id, playerCount);

  const hand = (received: { FEDERALIST: number; SEPARATION: number }) =>
    [
      received.FEDERALIST ? `${received.FEDERALIST} ${t.policies.FEDERALIST.short}` : null,
      received.SEPARATION ? `${received.SEPARATION} ${t.policies.SEPARATION.short}` : null,
    ]
      .filter(Boolean)
      .join(' and ');

  /** "Received 2 Federalist and 1 Separation; discarded a Separation Policy." */
  const describeClaim = (claim: SpeechClaim) => {
    if (claim.noComment) return claim.timedOut ? 'Declined to comment.' : 'No comment.';
    const got = `Received ${hand(claim.received)}`;
    return claim.discarded ? `${got}; discarded a ${policy(claim.discarded)}.` : `${got}; vetoed both.`;
  };

  /** Spec §74: the public record. Never includes hands, research results or roles. */
  const describeLog = (view: GameView, e: GameLogEntry): string | null => {
    const n = (id: string) => nameOf(view, id);
    switch (e.type) {
      case 'GAME_STARTED':
        return `${e.playerCount} politicians took their seats.`;
      case 'MINISTER_TOOK_OFFICE':
        return e.unityGovernment
          ? `${n(e.ministerPlayerId)} became ${minister} of a ${t.powers.UNITY_GOVERNMENT.name}.`
          : `${n(e.ministerPlayerId)} became ${minister}.`;
      case 'PREMIER_NOMINATED':
        return `${n(e.ministerPlayerId)} nominated ${n(e.premierPlayerId)} for ${premier}.`;
      case 'VOTE_RESOLVED':
        return e.passed
          ? `Government received ${t.phases.confidenceVote.confidence.toLowerCase()} ${e.confidence}–${e.noConfidence}.`
          : `Government lost the ${t.phases.confidenceVote.name.toLowerCase()} ${e.confidence}–${e.noConfidence}.`;
      case 'GRIDLOCK_ADVANCED':
        return `${t.phases.gridlock.short} ${e.gridlockCount} of ${rules(view.players.length).gridlockLimit}.`;
      case 'GRIDLOCK_TRIGGERED':
        return `${t.phases.gridlock.name}. ${t.phases.gridlock.title}.`;
      case 'POLICY_ENACTED':
        return e.source === 'GRIDLOCK' ? `A ${policy(e.policy)} was enacted automatically.` : `A ${policy(e.policy)} was enacted.`;
      case 'NENSHI_THRESHOLD_REACHED':
        return `${t.leader.momentumTitle}.`;
      case 'POWER_UNLOCKED':
        return `${factionPlural('FEDERALIST')} unlocked ${power(e.power)}.`;
      case 'VETO_UNLOCKED':
        return `The ${t.powers.VETO.name} is now available.`;
      case 'VETO_REQUESTED':
        return `The ${premier} proposed a ${t.powers.VETO.name}.`;
      case 'VETO_RESOLVED':
        return e.accepted ? `The ${minister} accepted the veto. Both policies were discarded.` : `The ${minister} refused the veto.`;
      case 'CAMPAIGN_RESEARCH':
        return `${n(e.ministerPlayerId)} conducted ${t.powers.CAMPAIGN_RESEARCH.name} on ${n(e.targetPlayerId)}.`;
      case 'INTERNAL_POLLING':
        return `${n(e.ministerPlayerId)} commissioned ${t.powers.INTERNAL_POLLING.name}.`;
      case 'UNITY_GOVERNMENT':
        return `${n(e.ministerPlayerId)} invited ${n(e.targetPlayerId)} to lead a ${t.powers.UNITY_GOVERNMENT.name}.`;
      case 'COURT_CHALLENGE':
        return `${t.powers.COURT_CHALLENGE.name}: ${n(e.targetPlayerId)} is out of play. ${t.phases.removed.title}.`;
      case 'DECK_RESHUFFLED':
        return 'The policy deck was reshuffled.';
      case 'SPEECH':
        return `${e.role === 'MINISTER' ? minister : premier} ${n(e.playerId)}: ${describeClaim(e.claim)}`;
      case 'QUESTION_PERIOD_ENDED': {
        const qp = t.phases.questionPeriod.name;
        return e.reason === 'VOTE' ? `The House voted to end ${qp}.` : e.reason === 'TIME' ? `${qp} ran out of time.` : `The host ended ${qp}.`;
      }
      case 'GAME_OVER':
        return `${win(e.winReason).headline}. ${win(e.winReason).winner}.`;
    }
  };

  /**
   * Why some players can't be nominated this round. Built only from public
   * state; mirrors EligibilityRules in the engine.
   */
  const eligibilityNote = (view: GameView): string | null => {
    const game = view.game;
    if (!game) return null;
    const { lastElectedMinisterPlayerId: lastMinister, lastElectedPremierPlayerId: lastPremier } = game;
    const current = game.currentMinisterPlayerId;
    const eligible = new Set(game.eligiblePremierPlayerIds);
    const active = game.players.filter((p) => p.active).length;
    const smallTable = active <= rules(game.players.length).smallTableActivePlayers;

    if (!lastMinister && !lastPremier) {
      return game.lastEnactment?.source === EnactmentSource.GRIDLOCK
        ? `${t.phases.gridlock.name} cleared the term limits: anyone except the ${minister} can be nominated.`
        : `Anyone except the ${minister} can be nominated.`;
    }
    const barred = [lastPremier, lastMinister].filter(
      (id, i, all): id is string =>
        !!id && id !== current && all.indexOf(id) === i && !eligible.has(id) && !!game.players.find((p) => p.id === id)?.active,
    );
    const parts: string[] = [];
    if (barred.length) {
      parts.push(
        `${joinNames(barred.map((id) => nameOf(view, id)))} served in the last approved government, so ${barred.length === 1 ? 'is' : 'are'} not eligible this round.`,
      );
    }
    if (smallTable && lastMinister && lastMinister !== current && eligible.has(lastMinister)) {
      parts.push(`With ${active} players left, the last ${minister} (${nameOf(view, lastMinister)}) may be nominated again.`);
    }
    return parts.length ? parts.join(' ') : null;
  };

  return {
    theme,
    t,
    minister,
    premier,
    faction,
    factionPlural,
    policy,
    power,
    powerShort,
    powerNarrator,
    win,
    rules,
    describeClaim,
    describeLog,
    eligibilityNote,
    character: (id: string) => getCharacter(theme.id, id),
    characterName: (id: string) => getCharacter(theme.id, id)?.displayName ?? id,
    gathered: (count: number) => fill(t.lobby.gathered, { count: NUMBER_WORDS[count] ?? count }),
  };
}

export type Copy = ReturnType<typeof makeCopy>;
